import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Clock, Calendar, ArrowUpRight, ArrowRight, Link2, Twitter, Linkedin, Facebook, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { trackEvent } from "@/lib/track";
import BlockRenderer from "@/components/public/BlockRenderer";
import ArticleCard from "@/components/public/ArticleCard";
import Newsletter from "@/components/public/Newsletter";
import { Reveal } from "@/components/public/Reveal";
import { BrushUnderline } from "@/components/public/Brush";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); } catch { return ""; }
}

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const post = data?.post;

  useSeo({
    title: post ? `${post.seo_title || post.title} | Lucrative Laptop` : "Lucrative Laptop",
    description: post?.meta_description || post?.excerpt, image: post?.social_image || post?.featured_image,
    canonical: post?.canonical_url, type: "article", noindex: post?.no_index,
  });

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/public/posts/${slug}`).then((r) => { setData(r.data); trackEvent("view", { slug, post_id: r.data.post.id }); }).catch(() => setNotFound(true));
  }, [slug]);

  const share = (network) => {
    const url = window.location.href; const title = post?.social_headline || post?.title || "";
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    if (links[network]) window.open(links[network], "_blank", "width=600,height=500");
  };
  const copyLink = () => { navigator.clipboard.writeText(window.location.href); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); };
  const ctaClick = (cta) => { trackEvent("cta_click", { post_id: post.id, target_id: cta.tracking_id || cta.id }); if (cta.url?.startsWith("http")) window.open(cta.url, "_blank", "noopener"); else window.location.href = cta.url; };

  if (notFound) return <div className="max-w-3xl mx-auto px-5 py-32 text-center"><h1 className="font-poster uppercase text-5xl text-white">Article not found</h1><Link to="/" className="text-brand mt-4 inline-block font-cond uppercase tracking-wider">Back home</Link></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-5 py-32 text-center text-brand font-mono-label" data-testid="article-loading">loading story...</div>;

  const cat = (post.category || "").replace(/-/g, " ").toUpperCase();
  const btnCls = "w-10 h-10 border border-ink-600 flex items-center justify-center text-white/70 hover:text-brand hover:border-brand transition-colors";

  return (
    <div data-testid="article-page">
      <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-brand origin-left z-[60]" style={{ scaleX: progress }} data-testid="reading-progress" />

      {/* social-first header */}
      <article className="max-w-[840px] mx-auto px-5 pt-10">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-6 font-cond font-bold uppercase tracking-[0.2em] text-xs">
            <Link to={`/category/${post.category}`} className="text-brand hover:text-brand-bright transition-colors">{cat}</Link>
            <span className="text-ink-600">/</span>
            <span className="text-white/50 flex items-center gap-1"><Clock size={12} /> {post.reading_time} MIN READ</span>
          </div>
          <h1 className="font-poster uppercase text-white leading-[0.92] text-4xl sm:text-5xl lg:text-6xl">{post.title}</h1>
          {post.subtitle && <p className="text-white/65 text-lg lg:text-xl mt-6 leading-relaxed max-w-2xl">{post.subtitle}</p>}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-8 pb-6 border-b border-ink-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-brand/50 flex items-center justify-center text-brand font-poster text-lg">{(post.author || "L")[0]}</div>
              <span className="font-cond uppercase tracking-wide text-white font-bold">{post.author}</span>
            </div>
            <span className="flex items-center gap-1.5 text-xs text-white/50 font-cond uppercase tracking-wider"><Calendar size={13} /> {fmtDate(post.publish_date)}</span>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={copyLink} data-testid="share-copy" className={btnCls}>{copied ? <Check size={15} /> : <Link2 size={15} />}</button>
              <button onClick={() => share("twitter")} data-testid="share-twitter" className={btnCls}><Twitter size={15} /></button>
              <button onClick={() => share("facebook")} data-testid="share-facebook" className={btnCls}><Facebook size={15} /></button>
              <button onClick={() => share("linkedin")} data-testid="share-linkedin" className={btnCls}><Linkedin size={15} /></button>
            </div>
          </div>
        </header>
      </article>

      {post.featured_image && (
        <Reveal>
          <figure className="max-w-[1200px] mx-auto px-5 mb-12">
            <div className="border border-ink-600 overflow-hidden group"><img src={post.featured_image} alt={post.featured_image_alt || post.title} className="w-full object-cover img-zoom" /></div>
            {post.featured_image_alt && <figcaption className="mt-2 font-cond uppercase tracking-wider text-xs text-white/45 text-center">{post.featured_image_alt}</figcaption>}
          </figure>
        </Reveal>
      )}

      <article className="max-w-[760px] mx-auto px-5">
        <BlockRenderer blocks={post.blocks} postId={post.id} />

        {data.affiliates?.length > 0 && (
          <div className="my-12 border border-ink-600 texture-concrete p-6">
            <div className="label-mono mb-4">// Tools mentioned</div>
            <div className="flex flex-col gap-4">
              {data.affiliates.map((a) => (
                <div key={a.id} className="flex items-center gap-4">
                  {a.image && <img src={a.image} alt={a.name} className="w-12 h-12 object-cover border border-ink-600" />}
                  <div className="flex-1 min-w-0"><div className="font-display font-bold uppercase text-white">{a.name}</div>{a.description && <div className="text-sm text-white/55 line-clamp-1">{a.description}</div>}</div>
                  <a href={a.affiliate_url || a.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackEvent("outbound_click", { post_id: post.id, target_id: a.tracking_name || a.id })} className="font-cond uppercase tracking-wider text-sm font-bold text-brand hover:text-brand-bright flex items-center gap-1 shrink-0">Visit <ArrowUpRight size={14} /></a>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-white/45">Some links are affiliate links. We only recommend tools we think are genuinely useful.</p>
          </div>
        )}

        {data.cta && (
          <div className="my-14 relative overflow-hidden border border-brand/50 texture-concrete p-8" data-testid="article-cta">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-brand" />
            <div className="relative pl-2">
              <div className="label-mono mb-3">// Tool worth knowing</div>
              <h3 className="font-poster uppercase text-white text-2xl lg:text-3xl leading-tight">{data.cta.headline}</h3>
              {data.cta.description && <p className="text-white/65 mt-3 text-base max-w-xl">{data.cta.description}</p>}
              <button onClick={() => ctaClick(data.cta)} className="btn-poster mt-6"><span>{(data.cta.button_text || "Check it out").toUpperCase()}</span><ArrowUpRight size={17} className="relative z-10" /></button>
              {data.cta.disclosure && <p className="mt-3 text-xs text-white/45">{data.cta.disclosure}</p>}
            </div>
          </div>
        )}

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-ink-700">
            {post.tags.map((t) => <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="font-cond uppercase tracking-wider text-xs text-white/70 border border-ink-600 px-3 py-1.5 hover:border-brand hover:text-brand transition-colors">#{t}</Link>)}
          </div>
        )}
      </article>

      <div className="max-w-[1200px] mx-auto px-5 lg:px-10 mt-16">
        {data.next && (
          <Link to={`/article/${data.next.slug}`} className="group flex items-center justify-between gap-6 border border-ink-600 texture-concrete p-7 lg:p-9 edge-card mb-16">
            <div>
              <div className="label-mono mb-2">// Read next</div>
              <h4 className="font-poster uppercase text-white text-xl lg:text-3xl leading-tight group-hover:text-brand transition-colors">{data.next.title}</h4>
            </div>
            <ArrowRight size={30} className="text-brand shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}

        {data.related?.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8"><span className="w-6 h-0.5 bg-brand" /><span className="label-mono">Related stories</span></div>
            <div className="grid sm:grid-cols-3 gap-6">{data.related.map((p, i) => <Reveal key={p.slug} delay={i * 0.06}><ArticleCard post={p} variant="default" /></Reveal>)}</div>
          </section>
        )}

        <div className="mb-8"><Newsletter compact /></div>
      </div>
    </div>
  );
}
